import React, { useEffect, useMemo, useRef, useState } from 'react';
import IUserList from '../../utils/UserList/UserList.interface';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch } from 'react-icons/fi';
import { IPreset } from '../../utils/ModulePresets/preset.interface';
import IModuleList from '../../utils/ModuleList/ModuleList.interface';
import { useModuleFuse, usePartnerFuse, usePresetFuse } from '../../hooks/Fuses/useFuse';

interface SpotlightSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnerList: IUserList[];
  presets: IPreset[];
  modules: IModuleList[];
  onSelect: (partner: IUserList) => void;
  onSelectPreset: (preset: IPreset) => void;
  onSelectModules: (module: IModuleList) => void;
}

const SpotlightSearchModal: React.FC<SpotlightSearchModalProps> = ({
  isOpen,
  onClose,
  partnerList,
  presets,
  modules,
  onSelect,
  onSelectPreset,
  onSelectModules
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [partnerResults, setPartnerResults] = useState<IUserList[]>([]);
  const [presetResults, setPresetResults] = useState<IPreset[]>([]);
  const [moduleResults, setModuleResults] = useState<IModuleList[]>([]);
  const SearchIcon = FiSearch as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

  const partnerFuse = usePartnerFuse(partnerList);
  const presetFuse = usePresetFuse(presets);
  const moduleFuse = useModuleFuse(modules);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setPartnerResults([]);
      setPresetResults([]);
      setModuleResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim()) {
      const partnersFound = partnerFuse.search(query).map((result) => result.item);
      const presetsFound = presetFuse.search(query).map((result) => result.item);
      const modulesFound = moduleFuse.search(query).map((result) => result.item);
      setPartnerResults(partnersFound);
      setPresetResults(presetsFound);
      setModuleResults(modulesFound);
    } else {
      setPartnerResults([]);
      setPresetResults([]);
      setModuleResults([]);
    }
  }, [query, partnerFuse, presetFuse, moduleFuse]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}>
          <motion.div
            className="bg-white/80 backdrop-blur-md border border-gray-200 shadow-2xl rounded-xl w-full max-w-xl p-4"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {/* Search Input with Icon */}
            <div className="relative">
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg placeholder-gray-400"
                placeholder="Fuzzy Search....."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* Results */}
            <div className="mt-4 max-h-[300px] overflow-y-auto custom-scrollbar">
              {query &&
                partnerResults.length === 0 &&
                presetResults.length === 0 &&
                moduleResults.length === 0 && (
                  <div className="text-center text-gray-500 py-4">No results found</div>
                )}

              {/* Partners Section */}
              {partnerResults.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">
                    PARTNERS
                  </div>
                  {partnerResults.map((partner) => (
                    <div
                      key={partner.key}
                      className="p-3 hover:bg-gray-100 cursor-pointer rounded-md transition"
                      onClick={() => {
                        onSelect(partner);
                        onClose();
                      }}>
                      <div className="font-medium">{partner.companyName}</div>
                      <div className="text-sm text-gray-500">{partner.email}</div>
                    </div>
                  ))}
                </>
              )}

              {/* Presets Section */}
              {presetResults.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2 px-2">
                    PRESETS
                  </div>
                  {presetResults.map((preset) => (
                    <div
                      key={preset.presetName}
                      className="p-3 hover:bg-gray-100 cursor-pointer rounded-md transition"
                      onClick={() => {
                        onSelectPreset(preset);
                        onClose();
                      }}>
                      <div className="font-medium">{preset.presetName}</div>
                      <div className="text-sm text-gray-500">
                        Modules: {preset.moduleList.length}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* MODULES */}
              {moduleResults.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2 px-2">
                    MODULES
                  </div>
                  {moduleResults.map((module) => (
                    <div
                      key={module.key}
                      className="p-3 hover:bg-gray-100 cursor-pointer rounded-md transition"
                      onClick={() => {
                        onSelectModules(module);
                        onClose();
                      }}>
                      <div className="font-medium">{module.moduleName}</div>
                      <div className="text-sm text-gray-500">
                        {module.isSystemModule
                          ? `Info: ${module.moduleInfo} - System Module`
                          : `Info: ${module.moduleInfo} - Base Module`}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SpotlightSearchModal;
